import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';

interface ThemeStore {
  theme: Theme;
  resolved: 'light' | 'dark'; // what's actually rendered
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

const STORAGE_KEY = 'mecadepot.theme';

function getPreferred(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function loadTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
  return stored ?? 'system';
}

function applyToDom(resolved: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  // Hint to the UA for native form controls + scrollbar colours.
  root.style.colorScheme = resolved;
}

const initialTheme = loadTheme();
const initialResolved = initialTheme === 'system' ? getPreferred() : initialTheme;
applyToDom(initialResolved);

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: initialTheme,
  resolved: initialResolved,
  setTheme: (t) => {
    localStorage.setItem(STORAGE_KEY, t);
    const resolved = t === 'system' ? getPreferred() : t;
    applyToDom(resolved);
    set({ theme: t, resolved });
  },
  toggle: () => {
    const next: Theme = get().resolved === 'dark' ? 'light' : 'dark';
    get().setTheme(next);
  },
}));

// Keep system theme in sync if the OS preference changes while the app is open.
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { theme, setTheme } = useThemeStore.getState();
    if (theme === 'system') setTheme('system');
  });
}
